// ======================================================================
// 📁 File: src/modules/ppe/components/engineering/product-family/ProductFamilyDrawer.jsx
// ======================================================================

import React, { useEffect, useState } from "react";
import axiosClient from "../../../../../api/axiosClient";

export default function ProductFamilyDrawer({
  open,
  mode = "create", // create | edit
  record = null,
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState({
    name: "",
    code: "",
    description: "",
    is_active: 1,
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // ------------------------------------------------------------
  // Initialize form
  // ------------------------------------------------------------
  useEffect(() => {
    if (mode === "edit" && record) {
      setForm({
        name: record.name || "",
        code: record.code || "",
        description: record.description || "",
        is_active: record.is_active ?? 1,
      });
    } else {
      setForm({
        name: "",
        code: "",
        description: "",
        is_active: 1,
      });
    }
  }, [mode, record]);

  if (!open) return null;

  const onChange = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
  };

  // ------------------------------------------------------------
  // Submit handler (CREATE + EDIT)
  // ------------------------------------------------------------
  const onSubmit = async () => {
    setError(null);

    if (!form.name || !form.code) {
      setError("Name and Code are mandatory");
      return;
    }

    setSaving(true);
    try {
      // ======================================================
      // CREATE
      // ======================================================
      if (mode === "create") {
        await axiosClient.post("/api/ppe/masters/product-families", {
          code: form.code,
          name: form.name,
          description: form.description,
          is_active: form.is_active,
        });
      }

      // ======================================================
      // EDIT  ✅ FIXED
      // ======================================================
      if (mode === "edit" && record?.id) {
        await axiosClient.put(
          `/api/ppe/masters/product-families/${record.id}`,
          {
            code: form.code,
            name: form.name,
            description: form.description,
            is_active: form.is_active,
          }
        );
      }

      onSaved?.();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        width: 420,
        height: "100vh",
        background: "#fff",
        boxShadow: "-4px 0 16px rgba(0,0,0,0.15)",
        zIndex: 2000,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ====================================================== */}
      {/* Header */}
      {/* ====================================================== */}
      <div
        style={{
          padding: 16,
          borderBottom: "1px solid #e5e7eb",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h6 style={{ margin: 0 }}>
          {mode === "create" ? "Create Product Family" : "Edit Product Family"}
        </h6>
        <button className="btn btn-sm btn-light" onClick={onClose}>
          ✕
        </button>
      </div>

      {/* ====================================================== */}
      {/* Body */}
      {/* ====================================================== */}
      <div style={{ padding: 16, flex: 1, overflowY: "auto" }}>
        {error && <div className="alert alert-danger">{error}</div>}

        <div className="mb-3">
          <label className="form-label">Name *</label>
          <input
            className="form-control"
            value={form.name}
            onChange={(e) => onChange("name", e.target.value)}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Code *</label>
          <input
            className="form-control"
            value={form.code}
            onChange={(e) => onChange("code", e.target.value)}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Description</label>
          <textarea
            className="form-control"
            rows={3}
            value={form.description}
            onChange={(e) => onChange("description", e.target.value)}
          />
        </div>

        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            checked={form.is_active === 1}
            onChange={(e) =>
              onChange("is_active", e.target.checked ? 1 : 0)
            }
          />
          <label className="form-check-label">Active</label>
        </div>
      </div>

      {/* ====================================================== */}
      {/* Footer */}
      {/* ====================================================== */}
      <div
        style={{
          padding: 16,
          borderTop: "1px solid #e5e7eb",
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
        }}
      >
        <button className="btn btn-outline-secondary" onClick={onClose}>
          Cancel
        </button>
        <button
          className="btn btn-primary"
          disabled={saving}
          onClick={onSubmit}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
